import React, { Component, ReactNode } from 'react';

import { ErrorContext, type ErrorContextValue } from '../lib/errorContextTypes';

import { ErrorFallback } from './ErrorFallback';

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  fallbackComponent?: React.ComponentType<{
    error: Error | null;
    onRetry: () => void;
    onReload?: () => void;
  }>;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
  level?: 'component' | 'section' | 'page';
  resetKeys?: Array<string | number | boolean | null | undefined>;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  static contextType = ErrorContext;
  declare context: ErrorContextValue | undefined;
  private resetTimeoutId: number | null = null;

  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    this.setState({ errorInfo });

    // Log the error
    console.error(`Error caught by ${this.props.level || 'component'} error boundary:`, error);
    console.error('Error details:', errorInfo);

    if (this.context) {
      try {
        this.context.addError({ message: error.message, severity: 'error', details: errorInfo });
      } catch (ctxErr) {
        console.error('Error reporting to context:', ctxErr);
      }
    }

    // Call custom error handler if provided
    if (this.props.onError) {
      try {
        this.props.onError(error, errorInfo);
      } catch (handlerError) {
        console.error('Error in custom error handler:', handlerError);
      }
    }
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps) {
    const { resetKeys } = this.props;
    const { hasError } = this.state;

    // Reset error state if resetKeys have changed
    if (hasError && resetKeys && resetKeys !== prevProps.resetKeys) {
      const hasResetKeyChanged = resetKeys.some(
        (key, index) => key !== prevProps.resetKeys?.[index]
      );

      if (hasResetKeyChanged) {
        this.resetErrorBoundary();
      }
    }
  }

  resetErrorBoundary = () => {
    // Clear any existing timeout
    if (this.resetTimeoutId) {
      clearTimeout(this.resetTimeoutId);
    }

    // Reset state immediately
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  handleRetry = () => {
    this.resetErrorBoundary();
  };

  handleReload = () => {
    window.location.reload();
  };

  handleResetApp = () => {
    try {
      localStorage.clear();
    } catch (e) {
      console.error('Failed to clear local storage:', e);
    }
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }
      const Fallback = this.props.fallbackComponent || ErrorFallback;
      return (
        <Fallback
          error={this.state.error}
          onRetry={this.handleRetry}
          onReload={this.handleReload}
        />
      );
    }

    return this.props.children;
  }
}

// Convenience wrapper for component-level error boundaries
export const ComponentErrorBoundary: React.FC<{
  children: ReactNode;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
  fallbackComponent?: React.ComponentType<{
    error: Error | null;
    onRetry: () => void;
    onReload?: () => void;
  }>;
}> = ({ children, onError, fallbackComponent }) => (
  <ErrorBoundary level="component" onError={onError} fallbackComponent={fallbackComponent}>
    {children}
  </ErrorBoundary>
);

// Convenience wrapper for section-level error boundaries
export const SectionErrorBoundary: React.FC<{
  children: ReactNode;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
  fallbackComponent?: React.ComponentType<{
    error: Error | null;
    onRetry: () => void;
    onReload?: () => void;
  }>;
}> = ({ children, onError, fallbackComponent }) => (
  <ErrorBoundary level="section" onError={onError} fallbackComponent={fallbackComponent}>
    {children}
  </ErrorBoundary>
);

// Convenience wrapper for page-level error boundaries
export const PageErrorBoundary: React.FC<{
  children: ReactNode;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
  fallbackComponent?: React.ComponentType<{
    error: Error | null;
    onRetry: () => void;
    onReload?: () => void;
  }>;
}> = ({ children, onError, fallbackComponent }) => (
  <ErrorBoundary level="page" onError={onError} fallbackComponent={fallbackComponent}>
    {children}
  </ErrorBoundary>
);
